import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  archiveProject,
  deleteProject,
  extractProjectBib,
  generateQuestionSet,
  getProjectBib,
  getProjectRun,
  getQuestionSetRun,
  patchProject,
  patchProjectFile,
  putProjectBib,
  runProject,
  saveResultMarks,
  searchProjectBib,
  type FileRole,
  type ImagePolicy,
  type ProjectCost,
  type ProjectDeepMark,
  type ProjectFigure,
  type ProjectFileRead,
  type ProjectRead,
  type ProjectResults,
  type SolutionVerification,
  type SourceReference,
  type SourceTier,
  type SummaryCard,
  type TaskSolution,
  type TopicSection,
} from "@/api/projects";
import { explainCard } from "@/api/lecture";
import { getPresentationPlan } from "@/api/presentation-plan";
import { saveSelectedSetId } from "@/train/selected-set";
import { useReviewedSections } from "./reviewed-sections-store";
import { Button, Spinner, Alert } from "@/components/ui";
import { MathText, RawMath } from "@/components/math-text";
import { SourceRefModal } from "./source-ref-modal";
import { SectionExpertPanel } from "./section-expert-panel";
import { BibEditorPanel, type BibApi } from "./report/bib-editor-panel";
import { useProject } from "./use-project";

const PROJECT_BIB_API: BibApi = {
  get: getProjectBib,
  extract: extractProjectBib,
  put: putProjectBib,
  search: searchProjectBib,
};
import { MarkdownLite, CodeBlock } from "./markdown-lite";
import { ParamExplorer } from "./explorer/param-explorer";
import { openPdfInStudy } from "./study-link";
import { DEPTH_LABELS, type Depth } from "@/api/lecture";

export function ProjectDetailPage() {
  const { t } = useTranslation();
  const { projectId = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: project, isLoading, error } = useProject(projectId);

  const archive = useMutation({
    mutationFn: (archived: boolean) => archiveProject(projectId, archived),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteProject(projectId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      navigate("/app/projects");
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }
  if (error || !project) return <Alert>{t("Could not load this project.")}</Alert>;

  const archived = !!project.archived_at;

  return (
    <div className="space-y-6">
      <Link to="/app/projects" className="text-sm text-brand-600 hover:underline">
        {t("← Projects")}
      </Link>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">{project.title}</h1>
        <div className="flex shrink-0 items-center gap-3">
          <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium capitalize text-zinc-600">
            {t(project.status)}
          </span>
          <button
            type="button"
            disabled={archive.isPending}
            onClick={() => archive.mutate(!archived)}
            className="text-sm font-medium text-ink-soft hover:text-ink"
          >
            {archived ? t("Restore") : t("Archive")}
          </button>
          <button
            type="button"
            disabled={remove.isPending}
            onClick={() => {
              if (confirm(t("Delete “{{title}}”? This cannot be undone.", { title: project.title }))) remove.mutate();
            }}
            className="text-sm font-medium text-rose-600 hover:text-rose-700"
          >
            {t("Delete")}
          </button>
        </div>
      </div>

      {project.status !== "running" && project.files.length > 0 && (
        <section className="card flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ink">
              {project.status === "complete" || project.status === "failed"
                ? t("Your marks")
                : t("Mark your files first (optional)")}
            </h2>
            <p className="text-sm text-ink-soft">
              {project.status === "complete" || project.status === "failed"
                ? t("Open the marking workspace to review or edit the marks you saved on this project.")
                : t("Open each uploaded file, highlight what matters, and save your progress. The agent focuses generation on exactly what you marked.")}
            </p>
          </div>
          <Button variant="secondary" onClick={() => navigate(`/app/projects/${projectId}/mark`)}>
            {project.status === "complete" || project.status === "failed" ? t("View marks") : t("Mark files")}
          </Button>
        </section>
      )}
      {/* Pre-complete: standalone report launch. Once the summary is complete the
          grouped "Create from this project" chooser (in Results) supersedes it. */}
      {project.files.length > 0 && project.status !== "complete" && (
        <section className="card flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ink">{t("Write a report")}</h2>
            <p className="text-sm text-ink-soft">
              {t("Turn this project's sources into an editable LaTeX report — the agent indexes every file, then drafts a document you edit and compile to PDF in the browser.")}
            </p>
          </div>
          <Button variant="secondary" onClick={() => navigate(`/app/projects/${projectId}/report`)}>
            {t("Open report writer")}
          </Button>
        </section>
      )}
      {(project.status === "running" || project.status === "draft" || project.status === "ready") && (
        <RunMonitor projectId={projectId} project={project} />
      )}
      {project.status === "failed" && <Alert>{t("The agent run failed.")} {project.result ? "" : t("Try recreating the project.")}</Alert>}
      {project.status === "complete" && project.result && <Results project={project} result={project.result} />}
    </div>
  );
}

function RunMonitor({
  projectId,
  project,
}: {
  projectId: string;
  project: ProjectRead;
}) {
  const { t } = useTranslation();
  const status = project.status;
  const hasFiles = project.files.length > 0;
  // A run only exists once one has been started. `getProjectRun` 404s when there
  // isn't one — treat that as "not started" (don't retry, don't keep polling)
  // instead of spinning forever.
  const { data, isError } = useQuery({
    queryKey: ["project-run", projectId],
    queryFn: () => getProjectRun(projectId),
    retry: false,
    refetchInterval: (q) => {
      if (q.state.error) return false; // 404 → no run yet; stop polling
      const s = (q.state.data?.status as string) || "";
      return s === "complete" || s === "failed" ? false : 3000;
    },
  });

  const runInFlight = !isError && !!data && (data.status === "pending" || data.status === "running");

  // No live run. Show the kickoff panel — unless the files are still being read
  // (draft with files = async extraction in progress), where we just wait.
  if (!runInFlight) {
    if (status === "draft" && hasFiles) {
      return (
        <div className="card flex items-center gap-2">
          <Spinner className="h-4 w-4" />
          <span className="text-sm text-ink-soft">{t("Reading your files…")}</span>
        </div>
      );
    }
    return <KickoffPanel projectId={projectId} project={project} />;
  }

  const STEP_LABELS: Record<string, string> = {
    "project.file-understand": "Reading material",
    "project.recap-synthesize": "Writing the recap",
    "project.task-solve": "Solving the task in code",
    "project.visualize": "Rendering diagrams",
    "cheatsheet.author": "Building summary cards",
    "generator.question-set": "Generating practice questions",
  };

  return (
    <div className="card space-y-3">
      <div className="flex items-center gap-2">
        <Spinner className="h-4 w-4" />
        <span className="text-sm font-medium text-ink">{t("The agent is working…")}</span>
      </div>
      <ol className="space-y-1">
        {(data?.steps ?? []).map((s) => (
          <li key={s.index} className="flex items-center gap-2 text-sm">
            <StatusDot status={s.status} />
            <span className={s.status === "complete" ? "text-ink-soft" : "text-ink"}>
              {STEP_LABELS[s.part_slug] ? t(STEP_LABELS[s.part_slug]) : s.part_slug}
            </span>
          </li>
        ))}
      </ol>
      <p className="text-xs text-ink-faint">{t("This can take a few minutes. You can leave and come back.")}</p>
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const color =
    status === "complete" ? "bg-emerald-500" : status === "failed" ? "bg-rose-500" : status === "running" ? "bg-amber-500" : "bg-zinc-300";
  return <span className={`inline-block h-2 w-2 rounded-full ${color}`} />;
}

// The pre-run kickoff panel — the sophisticated start point: a persistent
// brief ("what do you want?"), per-file role / tier / image-policy editing,
// and the Run button (the brief rides along and persists server-side).
function KickoffPanel({ projectId, project }: { projectId: string; project: ProjectRead }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [brief, setBrief] = useState(project.brief ?? "");

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["project", projectId] });
    qc.invalidateQueries({ queryKey: ["project-run", projectId] });
  };

  const saveBrief = useMutation({
    mutationFn: (b: string) => patchProject(projectId, { brief: b }),
    onSuccess: invalidate,
  });
  const editFile = useMutation({
    mutationFn: (v: {
      fileId: string;
      body: { role?: FileRole; image_policy?: ImagePolicy; source_tier?: SourceTier };
    }) => patchProjectFile(projectId, v.fileId, v.body),
    onSuccess: invalidate,
  });
  const start = useMutation({
    mutationFn: () => runProject(projectId, brief.trim() ? { brief: brief.trim() } : {}),
    onSuccess: invalidate,
  });

  const taskCount = project.files.filter((f) => f.role === "task").length;
  const gateHint =
    project.mode === "material"
      ? null
      : project.mode === "multi_sheet"
        ? taskCount >= 1
          ? null
          : t("Mark at least one file as a task sheet (currently {{count}}).", { count: taskCount })
        : taskCount === 1
          ? null
          : t("Mark exactly one file as the task (currently {{count}}).", { count: taskCount });

  const roles: FileRole[] = project.mode === "material" ? ["info", "ignore"] : ["info", "task", "ignore"];
  const seg = (active: boolean) =>
    "px-2.5 py-1 capitalize " + (active ? "bg-brand-600 text-white" : "bg-white text-ink-soft hover:bg-black/5");

  return (
    <section className="card space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-ink">{t("Generate the summary")}</h2>
        <p className="text-sm text-ink-soft">
          {project.mode === "material"
            ? t("Map the material, build summary cards and figures — the hub for practice sets, a report or a presentation.")
            : t("Solve the task, build summary cards, and create a practice set from this project.")}
        </p>
      </div>

      <label className="block">
        <span className="text-sm font-medium text-ink">{t("What do you want? (optional)")}</span>
        <textarea
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          onBlur={() => {
            if (brief.trim() !== (project.brief ?? "")) saveBrief.mutate(brief.trim());
          }}
          rows={2}
          maxLength={4000}
          placeholder={t("Explain in detail — the angle, emphasis, what to skip, what the output is for. This steers the summary, figures and questions.")}
          className="mt-1 w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm"
        />
      </label>

      {project.files.length > 0 && (
        <div>
          <span className="text-sm font-medium text-ink">{t("Files")}</span>
          <ul className="mt-1 divide-y divide-black/5">
            {project.files.map((f: ProjectFileRead) => (
              <li key={f.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                <span className="min-w-[8rem] flex-1 truncate text-sm text-ink" title={f.relative_path}>
                  {f.filename}
                </span>
                <div className="flex shrink-0 overflow-hidden rounded-lg border border-black/10 text-xs">
                  {roles.map((r) => (
                    <button
                      key={r}
                      type="button"
                      disabled={editFile.isPending}
                      onClick={() => f.role !== r && editFile.mutate({ fileId: f.id, body: { role: r } })}
                      className={seg(f.role === r)}
                    >
                      {r === "info" ? t("material") : t(r)}
                    </button>
                  ))}
                </div>
                {f.role === "info" && (
                  <div
                    className="flex shrink-0 overflow-hidden rounded-lg border border-black/10 text-xs"
                    title={t("Main sources are centered on; support files are background/reference only")}
                  >
                    {(["main", "support"] as SourceTier[]).map((tr) => (
                      <button
                        key={tr}
                        type="button"
                        disabled={editFile.isPending}
                        onClick={() =>
                          (f.source_tier ?? "main") !== tr &&
                          editFile.mutate({ fileId: f.id, body: { source_tier: tr } })
                        }
                        className={seg((f.source_tier ?? "main") === tr)}
                      >
                        {t(tr)}
                      </button>
                    ))}
                  </div>
                )}
                {f.role !== "ignore" && (
                  <div
                    className="flex shrink-0 overflow-hidden rounded-lg border border-black/10 text-xs"
                    title={t("Which images to pull from this file")}
                  >
                    <span className="bg-zinc-50 px-2 py-1 text-ink-faint">{t("Images")}</span>
                    {(["all", "none", "marked"] as ImagePolicy[]).map((ip) => (
                      <button
                        key={ip}
                        type="button"
                        disabled={editFile.isPending}
                        onClick={() =>
                          f.image_policy !== ip && editFile.mutate({ fileId: f.id, body: { image_policy: ip } })
                        }
                        className={seg(f.image_policy === ip)}
                      >
                        {t(ip)}
                      </button>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {gateHint && <p className="text-xs text-amber-700">{gateHint}</p>}
      {start.isError && <Alert>{t("Couldn't start the agent. Give it a moment and try again.")}</Alert>}
      <Button onClick={() => start.mutate()} disabled={!!gateHint || start.isPending}>
        {start.isPending ? t("Starting…") : t("Generate summary")}
      </Button>
    </section>
  );
}

function Results({ project, result }: { project: ProjectRead; result: ProjectResults }) {
  const { t } = useTranslation();

  const { reviewed, toggleSection } = useReviewedSections(project.id);
  // The prose region the user can highlight to mark passages to study.
  const proseRef = useRef<HTMLDivElement>(null);
  const topics = result.topic_sections ?? [];
  // Multi-sheet coursework projects carry one worked solution per task sheet.
  const hasMultiSolutions = (result.task_solutions?.length ?? 0) > 0;

  // Tappable solution source references → open the file at the passage.
  const [openRef, setOpenRef] = useState<SourceReference | null>(null);
  const fileById = new Map(project.files.map((f) => [f.id, f]));

  // Real figures grouped by topic slug (these carry caption + source_page, which
  // the bare topic `figure_urls` don't). Assign GLOBAL sequential figure numbers
  // across topics in study order so prose can reference "Fig. N".
  const figuresBySlug = new Map<string, ProjectFigure[]>();
  for (const f of result.figures) {
    if (!f.url || !f.topic_slug) continue;
    const arr = figuresBySlug.get(f.topic_slug) ?? [];
    arr.push(f);
    figuresBySlug.set(f.topic_slug, arr);
  }
  let figNo = 0;
  const topicBlocks = topics.map((sec) => {
    let figs = figuresBySlug.get(sec.slug) ?? [];
    // Fallback: topic carries figure_urls but no matching `figures` metadata.
    if (figs.length === 0 && sec.figure_urls.length > 0) {
      figs = sec.figure_urls.map((u) => ({ url: u, caption: null, topic_slug: sec.slug, source_page: null, cleaned: false }));
    }
    return { sec, figures: figs.map((fig) => ({ fig, number: ++figNo })) };
  });
  // Course → unit → topic grouping (Phase 4). Group only when units exist AND
  // topics carry a unit; otherwise render the flat list (back-compat).
  const courseUnits = result.units ?? [];
  const hasUnits = courseUnits.length > 0 && topicBlocks.some((b) => b.sec.unit);
  const renderTopicBlock = ({ sec, figures }: (typeof topicBlocks)[number]) => (
    <TopicSectionView
      key={sec.slug}
      projectId={project.id}
      section={sec}
      figures={figures}
      reviewed={reviewed.has(sec.slug)}
      onToggleReviewed={() => toggleSection(sec.slug)}
    />
  );
  const claimedSlugs = new Set(topics.map((s) => s.slug));
  // "Solved by <field>" badge (Phase 5) — shown when a field-expert handled the
  // solve. Tier-1 hand-coded → "specialist"; auto/curated profile → "expert".
  const specOrigin = result.specialization_origin ?? "";
  const fieldLabel = (result.field_label ?? "").trim();
  const specialistBadge =
    fieldLabel && specOrigin && specOrigin !== "generic"
      ? specOrigin === "specialist"
        ? t("Solved by the {{field}} specialist", { field: fieldLabel })
        : t("{{field}} expert", { field: fieldLabel })
      : null;
  // Real figures with no topic match → demoted to the "Other diagrams" catch-all.
  const orphanFigures = result.figures.filter((f) => f.url && (!f.topic_slug || !claimedSlugs.has(f.topic_slug)));
  const hasOtherDiagrams =
    orphanFigures.length > 0 || result.aux_image_urls.length > 0 || result.ai_image_urls.length > 0;

  return (
    <div className="space-y-6">
      {/* Markable prose region — highlight any passage here to mark it to study. */}
      <div ref={proseRef} className="space-y-6">
      {/* Header — field-expert badge + detected domain + subject tags */}
      {specialistBadge || (!!result.domain && result.domain !== "generic") || (result.subject_tags?.length ?? 0) > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {specialistBadge && (
            <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
              🎓 {specialistBadge}
            </span>
          )}
          {!!result.domain && result.domain !== "generic" && (
            <span className="rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-semibold capitalize text-brand-700">
              {result.domain}
            </span>
          )}
          {(result.subject_tags ?? []).map((tag) => (
            <span key={tag} className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium text-zinc-600">
              {tag}
            </span>
          ))}
        </div>
      ) : null}

      {/* Recap */}
      <section className="card">
        <h2 className="mb-2 text-lg font-semibold text-ink">{t("Lecture recap")}</h2>
        <MarkdownLite text={result.recap_markdown} />
        {result.key_points.length > 0 && (
          <div className="mt-3 rounded-lg bg-brand-50/60 p-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand-700">{t("Key points")}</p>
            <ul className="list-disc space-y-1 pl-5 text-sm text-ink-soft">
              {result.key_points.map((k, i) => (
                <li key={i}>
                  <MathText text={k} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Reviewed-sections progress — a local "work through it step by step"
          aid (localStorage only; does not touch the backend or generation). */}
      {topicBlocks.length > 0 && (
        <p className="text-xs font-medium text-ink-soft">
          {t("Reviewed {{n}} of {{total}} sections", {
            n: topicBlocks.filter(({ sec }) => reviewed.has(sec.slug)).length,
            total: topicBlocks.length,
          })}
        </p>
      )}

      {/* Per-topic spine — each topic's summary card + its own figures, in study
          order. Falls back to a flat figure gallery for pre-topic_sections results. */}
      {topicBlocks.length > 0 ? (
        hasUnits ? (
          <>
            {courseUnits.map((u) => {
              const blocks = topicBlocks.filter((b) => b.sec.unit === u.slug);
              if (blocks.length === 0) return null;
              return (
                <details key={u.slug} open className="space-y-3">
                  <summary className="cursor-pointer text-lg font-bold text-ink">{u.label}</summary>
                  {u.summary && <p className="text-sm text-ink-soft">{u.summary}</p>}
                  {blocks.map(renderTopicBlock)}
                </details>
              );
            })}
            {/* Topics with no (or unknown) unit → flat, after the grouped units. */}
            {topicBlocks
              .filter((b) => !b.sec.unit || !courseUnits.some((u) => u.slug === b.sec.unit))
              .map(renderTopicBlock)}
          </>
        ) : (
          topicBlocks.map(renderTopicBlock)
        )
      ) : (
        result.figures.length > 0 && <FiguresSection figures={result.figures} />
      )}

      {/* Background — the understanding behind the solution (field-specialized). */}
      {result.background && (
        <section className="card">
          <h2 className="mb-2 text-lg font-semibold text-ink">{t("Background — understand the solution")}</h2>
          <MarkdownLite text={result.background} />
        </section>
      )}

      {/* Solution — rendered by task kind. Code + program output are tucked behind
          a toggle (most quantitative projects return a derivation, no code). */}
      <section className="card">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink">
            {hasMultiSolutions ? t("Worked solutions") : t("Worked solution")}
          </h2>
          <div className="flex gap-2">
            {result.solution_pdf_url && (
              <a href={result.solution_pdf_url} target="_blank" rel="noreferrer" className="text-sm font-medium text-brand-700 hover:underline">
                {t("Download PDF")}
              </a>
            )}
            {result.solution_py_url && (
              <a href={result.solution_py_url} target="_blank" rel="noreferrer" className="text-sm font-medium text-brand-700 hover:underline">
                solution.py
              </a>
            )}
          </div>
        </div>

        {hasMultiSolutions ? (
          // Multi-sheet: one worked solution per task sheet.
          <div className="space-y-5">
            {result.task_solutions!.map((sol, i) => (
              <div key={sol.file_id || i} className={i > 0 ? "border-t border-black/5 pt-4" : ""}>
                <h3 className="mb-2 text-base font-semibold text-ink">
                  {sol.task_filename || t("Task {{n}}", { n: i + 1 })}
                </h3>
                <TaskSolutionBody sol={sol} />
              </div>
            ))}
          </div>
        ) : result.task_kind === "derivation" ? (
          <DerivationView result={result} />
        ) : (
          <>
            <MarkdownLite text={result.solution_markdown} />
            {result.plot_image_urls.length > 0 && <ImageGallery title={t("Plots")} items={result.plot_image_urls} />}
            {(result.code_blocks.length > 0 || result.stdout) && (
              <details className="mt-3">
                <summary className="cursor-pointer text-sm text-ink-soft">{t("Show code & program output")}</summary>
                <div className="mt-2">
                  {result.code_blocks.map((b, i) => (
                    <CodeBlock key={i} code={b.code} language={b.language} />
                  ))}
                  {result.stdout && (
                    <pre className="mt-2 overflow-x-auto rounded-lg bg-zinc-900 p-3 text-xs text-zinc-100">{result.stdout}</pre>
                  )}
                </div>
              </details>
            )}
          </>
        )}
        {/* Illustrative plots — rendered once for single solutions (multi-sheet
            renders per-sheet plots inside TaskSolutionBody). */}
        {!hasMultiSolutions && result.supplementary_image_urls.length > 0 && (
          <ImageGallery title={t("Illustrative plots")} items={result.supplementary_image_urls} />
        )}

        {/* Source references — tap to open the source file at the passage. */}
        {(result.source_references?.length ?? 0) > 0 && (
          <div className="mt-4 border-t border-black/5 pt-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand-700">{t("Source references")}</p>
            <p className="mb-2 text-xs text-ink-faint">{t("Tap a reference to open the source at the passage.")}</p>
            <ul className="space-y-1.5">
              {result.source_references!.map((ref, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => setOpenRef(ref)}
                    className="w-full rounded-lg border border-black/10 px-3 py-2 text-left text-sm hover:bg-brand-50"
                  >
                    <span className="font-medium text-brand-700">{ref.filename}</span>
                    {ref.context && <span className="text-ink-soft"> — {ref.context}</span>}
                    {ref.quote && <span className="mt-0.5 block truncate text-xs text-ink-faint">“{ref.quote}”</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Plots of the functions the material is about (project.function-plots). */}
      {(result.function_plot_image_urls?.length ?? 0) > 0 && (
        <section className="card">
          <h2 className="mb-2 text-lg font-semibold text-ink">{t("Plots of the key functions")}</h2>
          <ImageGallery title="" items={result.function_plot_image_urls!} />
        </section>
      )}

      {openRef && (
        <SourceRefModal
          file={fileById.get(openRef.file_id) ?? null}
          refItem={openRef}
          onClose={() => setOpenRef(null)}
        />
      )}

      {/* Project bibliography (canonical) — a generated report inherits it. */}
      <details className="card">
        <summary className="cursor-pointer text-lg font-semibold text-ink">{t("Bibliography")}</summary>
        <p className="mt-1 text-xs text-ink-faint">
          {t("Project references — auto-extract from the sources or add your own; a report inherits these.")}
        </p>
        <div className="mt-2 h-96 rounded-lg border border-black/10">
          <BibEditorPanel projectId={project.id} scope="project" bibApi={PROJECT_BIB_API} />
        </div>
      </details>

      {/* Other diagrams — generated concept maps + conceptual images + any real
          figure not tied to a topic. */}
      {hasOtherDiagrams && (
        <section className="card">
          <h2 className="mb-1 text-lg font-semibold text-ink">{t("Other diagrams")}</h2>
          <p className="mb-2 text-sm text-ink-soft">
            {t("Generated concept maps and diagrams, plus any figures not tied to a single topic.")}
          </p>
          <ImageGallery items={[...orphanFigures, ...result.aux_image_urls, ...result.ai_image_urls]} />
        </section>
      )}

      </div>
      {/* Mark the answer text → generate a focused study set. */}
      <ResultsMarking project={project} proseRef={proseRef} />

      <MarkSection project={project} result={result} />

      {/* Interactive explorer — generic, driven by the agent's parameter_spec */}
      {result.parameter_spec.length > 0 && (
        <section>
          <h2 className="mb-1 text-lg font-semibold text-ink">{t("Interactive explorer")}</h2>
          <p className="mb-3 text-sm text-ink-soft">
            {t("Drag the sliders — the solution is recomputed and the plots redraw for the values you choose.")}
          </p>
          <ParamExplorer projectId={project.id} spec={result.parameter_spec} />
        </section>
      )}

      {result.cost && <CostCard cost={result.cost} />}

      {/* Launch actions — the decoupled second phase. The summary above is the
          hub; each card builds a distinct artifact from it + the sources. */}
      <CreateFromProject project={project} />
    </div>
  );
}

// Unified "Create from this project" chooser (summary-complete). Groups the three
// downstream generators — a trainable question set, an editable LaTeX report, and
// an editable slide deck — each with its own optional brief.
function CreateFromProject({ project }: { project: ProjectRead }) {
  const { t } = useTranslation();
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-ink">{t("Create from this project")}</h2>
      <div className="grid items-start gap-3 lg:grid-cols-3">
        <QuestionSetCard project={project} />
        <ReportCard projectId={project.id} />
        <PresentationCard projectId={project.id} />
      </div>
    </section>
  );
}

// Report launch card — navigates to the report writer, which has its own brief
// field + options on its kickoff screen (so no brief is collected here).
function ReportCard({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <section className="card flex flex-col gap-3">
      <div>
        <h3 className="text-base font-semibold text-ink">{t("Report")}</h3>
        <p className="text-sm text-ink-soft">
          {t("Draft an editable LaTeX report from this project's sources, then edit and compile it to PDF in the browser.")}
        </p>
      </div>
      <Button
        variant="secondary"
        className="mt-auto"
        onClick={() => navigate(`/app/projects/${projectId}/report`)}
      >
        {t("Open report writer")}
      </Button>
    </section>
  );
}

// Presentation launch card — opens the conversational PLANNER (co-create a
// detailed, time-boxed delivery plan with Eddi). The plan can later be expanded
// into a slide deck from within the planner. The label reflects whether a plan
// already exists (the planner page itself resumes at the right stage).
function PresentationCard({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: plan } = useQuery({
    queryKey: ["presentation-plan", projectId],
    queryFn: () => getPresentationPlan(projectId),
    retry: false, // 404 = no plan yet — keep the default label
  });
  const label = !plan
    ? t("Plan a presentation")
    : plan.status === "planning" || plan.status === "failed"
      ? t("Continue planning")
      : t("Open presentation plan");
  return (
    <section className="card flex flex-col gap-3">
      <div>
        <h3 className="text-base font-semibold text-ink">{t("Presentation")}</h3>
        <p className="text-sm text-ink-soft">
          {t("Plan a talk with Eddi — set the length, pick focus areas, add material, and get a detailed, time-boxed delivery plan. Turn it into slides when you're ready.")}
        </p>
      </div>
      <Button
        className="mt-auto"
        onClick={() => navigate(`/app/projects/${projectId}/presentation-plan`)}
      >
        {label}
      </Button>
    </section>
  );
}

// Decoupled question-set generation. The summary is the prerequisite hub; this
// card drives the second phase: idle → "Generate", running → progress (polls the
// questions run), complete → "Open in Train". The backend reuses the summary run's
// outputs and folds in the marks (result_marks) + asked questions (result_qa).
function QuestionSetCard({ project }: { project: ProjectRead }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const status = project.question_set_status ?? "idle";
  const running = status === "running";
  // Prefilled from the project's persistent kickoff brief (editable per set —
  // the server also falls back to it when the field is left empty).
  const [brief, setBrief] = useState(project.brief ?? "");

  const gen = useMutation({
    mutationFn: () => {
      const b = brief.trim();
      return generateQuestionSet(project.id, b ? { brief: b } : {});
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project", project.id] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  // Poll the questions run while generating; refetch the project when it ends so
  // status flips to complete and produced_question_set_id appears.
  const { data: run } = useQuery({
    queryKey: ["project-qs-run", project.id],
    queryFn: () => getQuestionSetRun(project.id),
    enabled: running,
    refetchInterval: (q) => {
      const s = (q.state.data?.status as string) || "";
      return s === "complete" || s === "failed" ? false : 3000;
    },
  });
  useEffect(() => {
    const s = run?.status;
    if (s === "complete" || s === "failed") {
      qc.invalidateQueries({ queryKey: ["project", project.id] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    }
  }, [run?.status, project.id, qc]);

  const openInTrain = () => {
    if (project.produced_question_set_id) {
      saveSelectedSetId(project.produced_question_set_id);
      navigate("/app/train");
    }
  };

  if (status === "complete" && project.produced_question_set_id) {
    return (
      <section className="card flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-ink">{t("Re-derive this in practice")}</h2>
          <p className="text-sm text-ink-soft">
            {t("A training set was generated from this summary, your marks, and your questions.")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => gen.mutate()} loading={gen.isPending}>
            {t("Regenerate")}
          </Button>
          <Button onClick={openInTrain}>{t("Open in Train")}</Button>
        </div>
      </section>
    );
  }

  if (running) {
    return (
      <section className="card space-y-2">
        <div className="flex items-center gap-2">
          <Spinner className="h-4 w-4" />
          <span className="text-sm font-medium text-ink">{t("Generating your question set…")}</span>
        </div>
        <p className="text-xs text-ink-faint">
          {t("Reusing the summary, your marks, and your questions. This can take a few minutes — you can leave and come back.")}
        </p>
      </section>
    );
  }

  // idle or failed → offer generation.
  return (
    <section className="card flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="text-lg font-semibold text-ink">{t("Generate a question set")}</h2>
        <p className="text-sm text-ink-soft">
          {t("Build a trainable set from this summary — using the full source material, figures, image questions, and the passages + questions you marked while reading.")}
        </p>
        {status === "failed" && (
          <p className="mt-1 text-xs text-rose-600">{t("The last generation failed. Try again.")}</p>
        )}
        {gen.isError && (
          <p className="mt-1 text-xs text-rose-600">{(gen.error as Error)?.message}</p>
        )}
        <textarea
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          rows={2}
          placeholder={t("Brief (optional) — what you want, the angle, emphasis")}
          className="mt-2 w-full rounded-lg border border-black/10 px-3 py-2 text-sm sm:max-w-md"
        />
      </div>
      <Button onClick={() => gen.mutate()} loading={gen.isPending}>
        {t("Generate question set")}
      </Button>
    </section>
  );
}

// A single topic of the per-topic arc: heading + summary + summary card
// (formulas / methods / pitfalls) + this topic's figures, numbered and
// captioned with their source page so the prose can reference "Fig. N".
function TopicSectionView({
  projectId,
  section,
  figures,
  reviewed,
  onToggleReviewed,
}: {
  projectId: string;
  section: TopicSection;
  figures: { fig: ProjectFigure; number: number }[];
  reviewed: boolean;
  onToggleReviewed: () => void;
}) {
  const { t } = useTranslation();
  const card = section.summary_card;
  return (
    <section className={`card transition-opacity ${reviewed ? "opacity-70" : ""}`}>
      <div className="mb-1 flex items-start justify-between gap-3">
        <h2 className="text-lg font-semibold text-ink">
          <MathText text={section.label} />
        </h2>
        <button
          type="button"
          onClick={onToggleReviewed}
          aria-pressed={reviewed}
          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
            reviewed
              ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
              : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"
          }`}
        >
          {reviewed ? `✓ ${t("Reviewed")}` : t("Mark reviewed")}
        </button>
      </div>
      {section.summary && (
        <p className="mb-3 text-sm text-ink-soft">
          <MathText text={section.summary} />
        </p>
      )}
      {card && <SummaryCardView card={card} />}
      {figures.length > 0 && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {figures.map(({ fig, number }) => (
            <FigureCard key={number} fig={fig} number={number} />
          ))}
        </div>
      )}
      <SectionExpertPanel
        projectId={projectId}
        slug={section.slug}
        label={section.label}
        thread={section.expert_thread}
        figures={section.generated_figures}
      />
    </section>
  );
}

function SummaryCardView({ card }: { card: SummaryCard }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3 rounded-lg bg-zinc-50 p-3">
      {card.summary && <MarkdownLite text={card.summary} />}
      {card.key_formulas.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand-700">{t("Key formulas")}</p>
          <ul className="space-y-1 text-sm text-ink-soft">
            {card.key_formulas.map((f, i) => (
              <li key={i}>
                {f.label && <span className="font-medium text-ink">{f.label}: </span>}
                <RawMath text={f.expression} />
                {f.note && <span className="text-ink-faint"> — <MathText text={f.note} /></span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {card.key_methods.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand-700">{t("Methods")}</p>
          <ul className="space-y-2 text-sm text-ink-soft">
            {card.key_methods.map((m, i) => (
              <li key={i}>
                <span className="font-medium text-ink">{m.name}</span>
                {m.when_to_use && <span className="text-ink-faint"> — <MathText text={m.when_to_use} /></span>}
                {m.steps.length > 0 && (
                  <ol className="mt-1 list-decimal space-y-0.5 pl-5">
                    {m.steps.map((s, j) => (
                      <li key={j}>
                        <MathText text={s} />
                      </li>
                    ))}
                  </ol>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {card.common_pitfalls.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-700">{t("Common pitfalls")}</p>
          <ul className="list-disc space-y-1 pl-5 text-sm text-ink-soft">
            {card.common_pitfalls.map((p, i) => (
              <li key={i}>
                <MathText text={p} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function FigureCard({ fig, number }: { fig: ProjectFigure; number: number }) {
  const { t } = useTranslation();
  return (
    <figure className="m-0">
      <div className="relative">
        <img src={fig.url!} alt={fig.caption ?? ""} loading="lazy" className="w-full rounded-xl border border-black/10 object-contain" />
        {fig.source_page != null && (
          <span className="absolute right-2 top-2 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
            p.{fig.source_page}
            {fig.cleaned ? ` · ${t("cleaned")}` : ""}
          </span>
        )}
      </div>
      <figcaption className="mt-1 text-xs text-ink-faint">
        <span className="font-semibold text-ink-soft">{t("Fig. {{n}}", { n: number })}</span>
        {fig.caption ? ` — ${fig.caption}` : ""}
      </figcaption>
    </figure>
  );
}

const PART_LABELS: Record<string, string> = {
  "project.file-understand": "Read material",
  "project.recap-synthesize": "Recap",
  "project.task-solve": "Solve task",
  "project.visualize": "Diagrams (images)",
  "cheatsheet.author": "Summary cards",
  "generator.question-set": "Practice questions",
};

function usd(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return "$" + n.toFixed(n < 0.01 ? 5 : 4);
}

function CostCard({ cost }: { cost: ProjectCost }) {
  const { t } = useTranslation();
  return (
    <section className="card">
      <h2 className="mb-2 text-lg font-semibold text-ink">{t("Cost")}</h2>
      <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <div>
          <span className="text-ink-faint">{t("Provider cost")}</span>
          <div className="font-mono text-ink">{usd(cost.raw_total_usd)}</div>
        </div>
        <div>
          <span className="text-ink-faint">{t("Charged ({{markup}}×)", { markup: cost.markup })}</span>
          <div className="font-mono font-semibold text-ink">{usd(cost.charged_total_usd)}</div>
        </div>
        <div>
          <span className="text-ink-faint">{t("Tokens")}</span>
          <div className="font-mono text-ink-soft">
            {t("{{in}} in / {{out}} out", { in: (cost.prompt_tokens ?? 0).toLocaleString(), out: (cost.completion_tokens ?? 0).toLocaleString() })}
          </div>
        </div>
        <div>
          <span className="text-ink-faint">{t("Images")}</span>
          <div className="font-mono text-ink-soft">
            {cost.image_count} · {usd(cost.image_cost_usd)}
          </div>
        </div>
      </div>
      <details className="mt-3">
        <summary className="cursor-pointer text-sm text-ink-soft">{t("Per-step breakdown")}</summary>
        <table className="mt-2 w-full text-xs">
          <thead className="text-ink-faint">
            <tr className="text-left">
              <th className="py-1">{t("Step")}</th>
              <th>{t("Model")}</th>
              <th className="text-right">{t("In")}</th>
              <th className="text-right">{t("Out")}</th>
              <th className="text-right">{t("Cost")}</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {cost.steps.map((s, i) => (
              <tr key={i} className="border-t border-black/5">
                <td className="py-1 font-sans text-ink-soft">{PART_LABELS[s.part_slug] ? t(PART_LABELS[s.part_slug]) : s.part_slug}</td>
                <td className="text-ink-faint">{s.model ?? "—"}</td>
                <td className="text-right">{s.prompt_tokens ?? "—"}</td>
                <td className="text-right">{s.completion_tokens ?? "—"}</td>
                <td className="text-right">{usd(s.est_cost_usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
      <p className="mt-2 text-xs text-ink-faint">
        {t("Provider cost is the raw spend; charged applies the {{markup}}× markup. Image pricing is approximate — verify before billing.", { markup: cost.markup })}
      </p>
    </section>
  );
}

function FiguresSection({ figures }: { figures: ProjectFigure[] }) {
  const { t } = useTranslation();
  const shown = figures.filter((f) => f.url);
  if (shown.length === 0) return null;
  return (
    <section className="card">
      <h2 className="mb-1 text-lg font-semibold text-ink">{t("Figures from the material")}</h2>
      <p className="mb-3 text-sm text-ink-soft">{t("The actual diagrams from your files, picked and captioned by the agent.")}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {shown.map((f, i) => (
          <figure key={i} className="m-0">
            <div className="relative">
              <img src={f.url!} alt={f.caption ?? ""} loading="lazy" className="w-full rounded-xl border border-black/10 object-contain" />
              {f.source_page != null && (
                <span className="absolute right-2 top-2 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                  p.{f.source_page}
                  {f.cleaned ? ` · ${t("cleaned")}` : ""}
                </span>
              )}
            </div>
            {f.caption && <figcaption className="mt-1 text-xs text-ink-faint">{f.caption}</figcaption>}
          </figure>
        ))}
      </div>
    </section>
  );
}

function DerivationView({
  result,
}: {
  result: Pick<ProjectResults, "derivation_steps" | "final_answer"> & {
    verification?: SolutionVerification | null;
  };
}) {
  const { t } = useTranslation();
  return (
    <div>
      <ol className="space-y-3">
        {/* Each step is one display equation. RawMath strips any stray $…$/$$…$$
            wrapping (older sets emit wrapped LaTeX) and renders it as centered
            block math instead of cramped inline. */}
        {result.derivation_steps.map((s) => (
          <li key={s.order} className="border-l-2 border-brand-200 pl-3">
            <div className="overflow-x-auto text-sm">
              <RawMath text={s.latex} display />
            </div>
            {s.explanation && (
              <p className="mt-1 text-xs text-ink-soft">
                <MathText text={s.explanation} />
              </p>
            )}
          </li>
        ))}
      </ol>
      {result.final_answer && (
        <div className="mt-4 rounded-lg bg-brand-50/60 p-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand-700">{t("Final answer")}</p>
          <RawMath text={result.final_answer} display />
        </div>
      )}
      {result.verification && (
        <p
          className={
            "mt-3 inline-block rounded-md px-2 py-0.5 text-xs font-medium " +
            (result.verification.passed ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")
          }
        >
          {result.verification.passed ? t("✓ Symbolically verified (sympy)") : t("Symbolic check did not confirm")}
        </p>
      )}
    </div>
  );
}

// One task sheet's worked solution (multi_sheet projects). Mirrors the single
// solution body but scoped to one TaskSolution.
function TaskSolutionBody({ sol }: { sol: TaskSolution }) {
  const { t } = useTranslation();
  return (
    <div>
      {sol.background && (
        <div className="mb-2">
          <MarkdownLite text={sol.background} />
        </div>
      )}
      {sol.task_kind === "derivation" ? (
        <DerivationView result={sol} />
      ) : (
        <>
          <MarkdownLite text={sol.narrative} />
          {sol.plot_image_urls.length > 0 && <ImageGallery title={t("Plots")} items={sol.plot_image_urls} />}
          {(sol.code_blocks.length > 0 || sol.stdout) && (
            <details className="mt-3">
              <summary className="cursor-pointer text-sm text-ink-soft">{t("Show code & program output")}</summary>
              <div className="mt-2">
                {sol.code_blocks.map((b, i) => (
                  <CodeBlock key={i} code={b.code} language={b.language} />
                ))}
                {sol.stdout && (
                  <pre className="mt-2 overflow-x-auto rounded-lg bg-zinc-900 p-3 text-xs text-zinc-100">{sol.stdout}</pre>
                )}
              </div>
            </details>
          )}
        </>
      )}
      {sol.supplementary_image_urls.length > 0 && (
        <ImageGallery title={t("Illustrative plots")} items={sol.supplementary_image_urls} />
      )}
    </div>
  );
}

// Per-depth heat colours for the floating mark bar (Full=amber, Brief=blue,
// Just test=green) — mirrors the Study marking workspace.
const DEPTH_BTN: Record<Depth, string> = {
  from_scratch: "bg-amber-100 text-amber-800 hover:bg-amber-200",
  recap_briefly: "bg-blue-100 text-blue-800 hover:bg-blue-200",
  already_know: "bg-green-100 text-green-800 hover:bg-green-200",
};
const DEPTHS: Depth[] = ["from_scratch", "recap_briefly", "already_know"];

// Mark passages in the project's RESPONSE TEXT. Select prose in `proseRef` → a
// floating Full/Brief/Just-test bar → marks collect in a panel (autosaved
// server-side as result_marks). Generating a question set here triggers the SAME
// decoupled pipeline as the main card: the backend reuses the summary run and
// folds these marks (+ asked questions) into generation.
function ResultsMarking({
  project,
  proseRef,
}: {
  project: ProjectRead;
  proseRef: React.RefObject<HTMLDivElement>;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [marks, setMarks] = useState<ProjectDeepMark[]>(project.result_marks?.deep ?? []);
  // Specialist deep-card lookups on the summary ("Explain in depth").
  const [lookups, setLookups] = useState<ProjectDeepMark[]>(project.result_marks?.lookups ?? []);
  const [bar, setBar] = useState<{ top: number; left: number; text: string } | null>(null);
  const [generating, setGenerating] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persist = useCallback(
    (deep: ProjectDeepMark[], look: ProjectDeepMark[]) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        void saveResultMarks(project.id, { deep, lookups: look }).catch(() => {});
      }, 600);
    },
    [project.id],
  );

  // Show the floating bar when the user has selected prose inside the results.
  useEffect(() => {
    const onUp = (e: MouseEvent) => {
      const root = proseRef.current;
      // Ignore clicks inside our own bar/panel (they carry data-no-mark).
      if (e.target instanceof Element && e.target.closest("[data-no-mark]")) return;
      const sel = window.getSelection();
      const text = sel?.toString().trim() ?? "";
      if (!sel || !text || sel.rangeCount === 0 || !root || !root.contains(sel.anchorNode)) {
        setBar(null);
        return;
      }
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        setBar(null);
        return;
      }
      setBar({ top: rect.top, left: rect.left + rect.width / 2, text });
    };
    document.addEventListener("mouseup", onUp);
    return () => document.removeEventListener("mouseup", onUp);
  }, [proseRef]);

  const addMark = (depth: Depth) => {
    if (!bar) return;
    const text = bar.text;
    const mark: ProjectDeepMark = {
      id: `${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
      selected_text: text.slice(0, 600),
      label: text.slice(0, 60),
      depth,
    };
    const next = [...marks, mark];
    setMarks(next);
    persist(next, lookups);
    setBar(null);
    window.getSelection()?.removeAllRanges();
  };

  // "In depth" — a field specialist authors a deep card for the selected passage,
  // stored as a result-mark lookup (carries the facets into summary→set).
  const explainInDepth = async () => {
    if (!bar) return;
    const text = bar.text;
    setExplaining(true);
    setError(null);
    try {
      const card = await explainCard({
        selectedText: text.slice(0, 2000),
        context: proseRef.current?.innerText?.slice(0, 6000) || null,
        language: "en",
        domain: project.result?.domain ?? null,
        fieldPath: (project.result as { field_path?: string } | null)?.field_path ?? null,
      });
      const display = [
        card.definition && `${t("Definition")}\n${card.definition}`,
        card.explanation && `${t("Explanation")}\n${card.explanation}`,
        card.intuition && `${t("Intuition")}\n${card.intuition}`,
        card.question &&
          `${t("Check yourself")}\n${card.question}${card.answer ? `\n\n${card.answer}` : ""}`,
      ]
        .filter(Boolean)
        .join("\n\n");
      const lk: ProjectDeepMark = {
        id: `${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
        kind: "general",
        selected_text: text.slice(0, 600),
        label: text.slice(0, 60),
        explanation: display,
        definition: card.definition,
        intuition: card.intuition,
        question: card.question,
        answer: card.answer,
      };
      const next = [...lookups, lk];
      setLookups(next);
      persist(marks, next);
      setBar(null);
      window.getSelection()?.removeAllRanges();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Couldn't explain that."));
    } finally {
      setExplaining(false);
    }
  };

  const remove = (id: string) => {
    const next = marks.filter((m) => m.id !== id);
    setMarks(next);
    persist(next, lookups);
  };

  const removeLookup = (id: string) => {
    const next = lookups.filter((m) => m.id !== id);
    setLookups(next);
    persist(marks, next);
  };

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      // Flush the debounced autosave so the run reads the latest marks, then
      // kick off the decoupled question-set generation (which folds result_marks
      // + result_qa into the full pipeline server-side). The QuestionSetCard
      // above shows progress once the project query refetches.
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await saveResultMarks(project.id, { deep: marks, lookups });
      await generateQuestionSet(project.id, {});
      await qc.invalidateQueries({ queryKey: ["project", project.id] });
      await qc.invalidateQueries({ queryKey: ["projects"] });
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Could not start generation."));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      {bar && (
        <div
          data-no-mark
          className="fixed z-50 -translate-x-1/2 -translate-y-full rounded-lg border border-black/10 bg-white p-1 shadow-soft"
          style={{ top: bar.top - 6, left: bar.left }}
          onPointerDown={(e) => e.preventDefault()}
        >
          <div className="flex gap-1">
            {DEPTHS.map((dp) => (
              <button
                key={dp}
                className={`rounded px-2.5 py-1 text-xs font-semibold ${DEPTH_BTN[dp]}`}
                onClick={() => addMark(dp)}
              >
                {t(DEPTH_LABELS[dp])}
              </button>
            ))}
            <button
              className="rounded bg-brand-100 px-2.5 py-1 text-xs font-semibold text-brand-700 hover:bg-brand-200 disabled:opacity-50"
              onClick={() => void explainInDepth()}
              disabled={explaining}
            >
              {explaining ? t("Explaining…") : t("In depth")}
            </button>
          </div>
        </div>
      )}

      <section className="card" data-no-mark>
        <h2 className="mb-1 text-lg font-semibold text-ink">{t("Mark the answer to study it")}</h2>
        <p className="mb-3 text-sm text-ink-soft">
          {t("Select any passage above, then choose Full, Brief, or Just test. Generate a question set from exactly what you marked.")}
        </p>
        {error && <Alert>{error}</Alert>}
        {marks.length === 0 ? (
          <p className="text-sm text-ink-faint">{t("No marked passages yet.")}</p>
        ) : (
          <ul className="space-y-1">
            {marks.map((m) => (
              <li key={m.id} className="flex items-start justify-between gap-2 text-sm">
                <span className="min-w-0 text-ink-soft">
                  <span className="mr-2 rounded bg-black/5 px-1.5 py-0.5 text-[11px] font-semibold text-ink-faint">
                    {t(DEPTH_LABELS[(m.depth ?? "from_scratch") as Depth])}
                  </span>
                  {m.selected_text}
                </span>
                <button type="button" onClick={() => remove(m.id)} className="shrink-0 text-rose-600">
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
        {lookups.length > 0 && (
          <ul className="mt-3 space-y-2">
            {lookups.map((l) => (
              <li key={l.id} className="rounded-md border border-black/10 bg-brand-50/40 p-2 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <span className="min-w-0 font-medium text-ink">{l.label || l.selected_text}</span>
                  <button type="button" onClick={() => removeLookup(l.id)} className="shrink-0 text-rose-600">
                    ✕
                  </button>
                </div>
                {l.explanation && (
                  <div className="mt-1 whitespace-pre-wrap text-ink-soft">
                    <MathText text={l.explanation} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3">
          <Button
            onClick={generate}
            loading={generating}
            disabled={generating || (marks.length === 0 && lookups.length === 0)}
          >
            {t("Generate a study set from these marks")}
          </Button>
        </div>
      </section>
    </>
  );
}

function MarkSection({ project, result }: { project: ProjectRead; result: ProjectResults }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const targets: { key: string; label: string; url: string }[] = [];
  if (result.solution_pdf_url) {
    targets.push({ key: "solution", label: t("Worked solution (generated PDF)"), url: result.solution_pdf_url });
  }
  for (const f of project.files) {
    if (f.file_type === "pdf" && f.download_url) {
      targets.push({ key: f.id, label: f.filename, url: f.download_url });
    }
  }
  if (targets.length === 0) return null;

  const openIt = async (key: string, url: string, label: string) => {
    setError(null);
    setBusy(key);
    try {
      const ref = await openPdfInStudy(url, label);
      navigate(`/app/study/${ref}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Could not open that PDF."));
      setBusy(null);
    }
  };

  return (
    <section className="card">
      <h2 className="mb-1 text-lg font-semibold text-ink">{t("Mark & fine-tune")}</h2>
      <p className="mb-3 text-sm text-ink-soft">
        {t("Open any of these in the marking workspace. Highlight what matters, then generate a focused question set from your marks to drill exactly where you need it.")}
      </p>
      {error && <Alert>{error}</Alert>}
      <ul className="divide-y divide-black/5">
        {targets.map((tg) => (
          <li key={tg.key} className="flex items-center justify-between gap-3 py-2">
            <span className="truncate text-sm text-ink" title={tg.label}>
              {tg.label}
            </span>
            <Button size="sm" variant="secondary" disabled={busy === tg.key} onClick={() => openIt(tg.key, tg.url, tg.label)}>
              {busy === tg.key ? t("Opening…") : t("Mark & study")}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ImageGallery({ title, items }: { title?: string; items: { url: string | null; caption: string | null }[] }) {
  const shown = items.filter((it) => it.url);
  if (shown.length === 0) return null;
  return (
    <div className="mt-3">
      {title && <h3 className="mb-2 text-sm font-semibold text-ink-soft">{title}</h3>}
      <div className="grid gap-3 sm:grid-cols-2">
        {shown.map((it, i) => (
          <figure key={i} className="m-0">
            <img src={it.url!} alt={it.caption ?? ""} loading="lazy" className="w-full rounded-xl border border-black/10 object-contain" />
            {it.caption && <figcaption className="mt-1 text-xs text-ink-faint">{it.caption}</figcaption>}
          </figure>
        ))}
      </div>
    </div>
  );
}
